// src/area/ResultadosArea/ResultadosArea.jsx
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  lazy,
  Suspense,
  startTransition,
  useRef
} from "react";
import axios from "axios";
import moment from "moment";
import { Tab } from "@headlessui/react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { FixedSizeGrid as Grid } from "react-window";

import { toast } from "react-hot-toast";
import { FaFilePdf } from "react-icons/fa";

import API_BASE_URL from "../../config/config.js";
import {
  downloadResultadosFechaPdf,
  resultadosPdfErrorMessage,
  STANDINGS_STALE_WARNING,
} from "./resultadosFechaPdfService.js";
import { setMatchSelected } from "../../features/matchSlice.js";
import { formatIdSport } from "../../utils/formatIdSport.js";
import useWindowSize from "../../hooks/useWindowSize.js";
import MatchCard from "./MatchCard.jsx";
import AutoSizer from "react-virtualized-auto-sizer";
import "./ResultadosArea.css";

/* ============ Carga perezosa de modales ============ */
const FullScreenResultados = lazy(() =>
  import("../../components/FullScreenModal/FullScreenResultados/FullScreenResultados.jsx")
);
const FullScreenFut = lazy(() =>
  import("../../components/FullScreenModal/FullScreenFut/FullScreenFut.jsx")
);
const FullScreenVoley = lazy(() =>
  import("../../components/FullScreenModal/FullScreenFut/FullScreenVoley.jsx")
);
const FullScreenSimpleScore = lazy(() =>
  import("../../components/FullScreenModal/FullScreenSimpleScore/FullScreenSimpleScore.jsx")
);
const FullScreenPlanillaHandball = lazy(() =>
  import("../../components/FullScreenModal/FullScreenPlanilla/Handball/FullScreenPlanillaHandball.jsx")
)
const FullScreenPlanillaBasquetball = lazy(() =>
  import("../../components/FullScreenModal/FullScreenPlanilla/Basquetball/FullScreenPlanillaBasquetball.jsx")
)
const FullScreenPlanillaFutbol = lazy(() =>
  import("../../components/FullScreenModal/FullScreenPlanilla/Futbol/FullScreenPlanillaFutbol.jsx")
)
const FullScreenPlanillaFutsal = lazy(() =>
  import("../../components/FullScreenModal/FullScreenPlanilla/Futsal/FullScreeenPlanillaFutsal.jsx")
)
const FullScreenPlanillaRugby = lazy(() =>
  import("../../components/FullScreenModal/FullScreenPlanilla/Rugby/FullScreenPlanillaRugby.jsx")
)
const FullScreenPlanillaVoleyball = lazy(() =>
  import("../../components/FullScreenModal/FullScreenPlanilla/Voleyball/FullScreenPlanillaVoleyball.jsx")
)
const FullScreenPlanillaHandballBeach = lazy(() =>
  import("../../components/FullScreenModal/FullScreenPlanilla/HandballBeach/FullScreenPlanillaHandballBeach.jsx")
)

function ResultadosArea() {
  /* ---------- State ---------- */
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState("1");

  const gridRef = useRef(null);
  const scrollTopRef = useRef(0);
  const [refreshing, setRefreshing] = useState(false);

  const [config, setConfig] = useState({
    nroFechasGrupo: 0,
    nroEtapasFinal: 0,
    moodName: 1,
    useBandera: false,
  });

  const [fechas, setFechas] = useState([]);
  const [etapas, setEtapas] = useState([]);

  const [matches, setMatches] = useState([]);
  const [sport, setSport] = useState(null);
  const [readyToFetchMatches, setReadyToFetchMatches] = useState(false);

  const [loading, setLoading] = useState(true);
  const [dots, setDots] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  /* ---------- UI modals ---------- */
  const [openResultados, setOpenResultados] = useState(false);
  const [openStatsFut, setOpenStatsFut] = useState(false);
  const [openStatsVoley, setOpenStatsVoley] = useState(false);
  const [openSimpleScore, setOpenSimpleScore] = useState(false);
  const [simpleScoreMatch, setSimpleScoreMatch] = useState(null);
  const [openPlanillaVivo, setOpenPlanillaVivo] = useState(false);
  const [openPlanillaBasquetball, setOpenPlanillaBasquetball] = useState(false);
  const [openPlanillaFutbol, setOpenPlanillaFutbol] = useState(false);
  const [openPlanillaFutsal, setOpenPlanillaFutsal] = useState(false);
  const [openPlanillaRugby, setOpenPlanillaRugby] = useState(false);
  const [openPlanillaVoleyball, setOpenPlanillaVoleyball] = useState(false);
  const [openPlanillaHandballBeach, setOpenPlanillaHandballBeach] = useState(false);
  const [tipoStat, setTipoStat] = useState(null);

  const { idevent, idsport } = useParams();
  const dispatch = useDispatch();
  // Familia del deporte de la ruta: decide QUÉ planilla se abre. Debe leerse de la ruta actual, no de una captura vieja
  // (FTR-0058: el selector de deporte reutiliza este componente con otro :idsport y el callback guardaba el deporte anterior).
  const formattedSport = formatIdSport(idsport);

  /* ---------- Fetch Config + Sport al montar ---------- */
  useEffect(() => {
    if (!idevent || !idsport) return;

    const ctrConfig = new AbortController();
    const ctrSport = new AbortController();

    setReadyToFetchMatches(false);
    setLoading(true);
    setMatches([]);
    setSelectedDate("1");

    (async () => {
      try {
        const [cfgRes, sportRes] = await Promise.all([
          axios.post(
            `${API_BASE_URL}/config_category`,
            { idevent, idsport },
            { signal: ctrConfig.signal }
          ),
          axios.get(
            `${API_BASE_URL}/sport/?idsport=${idsport}`,
            { signal: ctrSport.signal }
          ),
        ]);

        const cfg = cfgRes.data;
        const nroFechasGrupo = Number(cfg.nro_fechas_grupo || 0);
        const nroEtapasFinal = Number(cfg.nro_etapas_final || 0);

        setConfig({
          nroFechasGrupo,
          nroEtapasFinal,
          moodName: cfg.moodName,
          useBandera: cfg.useBandera,
        });

        setFechas(Array.from({ length: nroFechasGrupo }, (_, i) => `${i + 1}`));
        setEtapas([
          "Octavos",
          "Cuartos",
          "Semis",
          "Final",
        ].slice(4 - nroEtapasFinal));

        setSport(sportRes.data);
        setReadyToFetchMatches(true);
      } catch (err) {
        if (axios.isCancel(err)) return;
        console.error(err);
        setLoading(false);
      }
    })();

    return () => {
      ctrConfig.abort();
      ctrSport.abort();
    };
  }, [idevent, idsport]);

  const typeSportNumber = useMemo(() => Number(sport?.typeSport), [sport?.typeSport]);

  const hasFinalPhaseConfigured = useMemo(
    () => Number(config.nroEtapasFinal) > 0,
    [config.nroEtapasFinal]
  );

  const shouldUseOldNoPhaseFilter = useMemo(() => {
    return (
      typeSportNumber === 3 ||
      (typeSportNumber === 2 && !hasFinalPhaseConfigured)
    );
  }, [typeSportNumber, hasFinalPhaseConfigured]);

  const getCurrentIdTypePhase = useCallback(() => {
    const hasGroupPhase = Number(config.nroFechasGrupo) > 0;
    const hasFinalPhase = Number(config.nroEtapasFinal) > 0;

    if (!hasGroupPhase && hasFinalPhase) return 2;

    if (+idevent === 16) {
      return hasGroupPhase ? (selectedIndex === 0 ? 1 : 2) : 2;
    }

    return selectedIndex === 0 ? 1 : 2;
  }, [config.nroFechasGrupo, config.nroEtapasFinal, idevent, selectedIndex]);

  /* ---------- Fetch Matches cada vez que cambian pestaña / fecha ---------- */
  // useEffect(() => {
  //   // Lógica condicional para idevent 16
  //   let idtypephase;
  //   if (+idevent === 16) {
  //     if (config.nroFechasGrupo > 0) {
  //       idtypephase = selectedIndex === 0 ? 1 : 2;
  //     } else {
  //       idtypephase = 2;
  //     }
  //   } else {
  //     idtypephase = selectedIndex === 0 ? 1 : 2;
  //   }

  //   let isMounted = true;
  //   const ctr = new AbortController();

  //   (async () => {
  //     try {
  //       const body = {
  //         idevent,
  //         idsport,
  //         nro_fecha: selectedDate,
  //         ...(sport?.typeSport === 1 && { idtypephase }),
  //       };

  //       const { data } = await axios.post(
  //         `${API_BASE_URL}/getmatchesperiod`,
  //         body,
  //         { signal: ctr.signal }
  //       );

  //       if (!isMounted) return;

  //       startTransition(() => {
  //         setMatches(data);
  //         setLoading(false);
  //       });
  //     } catch (err) {
  //       if (axios.isCancel(err)) return;
  //       console.error(err);
  //     }
  //   })();

  //   return () => {
  //     isMounted = false;
  //     ctr.abort();
  //   };
  // }, [selectedIndex, selectedDate, idevent, idsport, sport?.typeSport, config.nroFechasGrupo]);


  // 1) FACTORIZA tu fetch en un callback reutilizable

  const fetchMatches = useCallback(async ({ signal, silent = false } = {}) => {
    const idtypephase = getCurrentIdTypePhase();

    const body = {
      idevent,
      idsport,
      nro_fecha: Number(selectedDate),
      idtypephase,
      isFromWinscore: true
    };

    if (shouldUseOldNoPhaseFilter) {
      delete body.idtypephase;
    }

    if (!silent) setLoading(true); else setRefreshing(true);


    try {
      const { data } = await axios.post(`${API_BASE_URL}/getmatchesperiod`, body, { signal });
      startTransition(() => setMatches(data));
    } catch (err) {
      if (!axios.isCancel(err)) console.error(err);
    } finally {
      if (!silent) setLoading(false); else setRefreshing(false);
    }
  }, [
    idevent,
    idsport,
    selectedDate,
    getCurrentIdTypePhase,
    shouldUseOldNoPhaseFilter,
  ])

  // 2) useEffect inicial / cada cambio de pestaña/fecha
  useEffect(() => {
    if (!idevent || !idsport) return;
    if (!readyToFetchMatches) return;
    if (sport?.typeSport == null) return;

    const ctr = new AbortController();
    fetchMatches({ signal: ctr.signal, silent: false });
    return () => ctr.abort();
  }, [fetchMatches, idevent, idsport, readyToFetchMatches, sport?.typeSport]);


  // 3) Un refresco invocable desde el modal
  // const refreshMatches = useCallback(() => {
  //   const ctr = new AbortController();
  //   fetchMatches({ signal: ctr.signal, silent: true }); // 👈 sin Skeleton, no desmonta la Grid
  // }, [fetchMatches]);
  const refreshMatches = useCallback(() => {
    const ctr = new AbortController();
    return fetchMatches({ signal: ctr.signal, silent: true });
  }, [fetchMatches]);


  /* ---------- Resto de hooks y render tal cual ---------- */
  useEffect(() => {
    setSelectedDate("1");
  }, [selectedIndex]);

  useEffect(() => {
    if (!loading) return;
    const frames = ["", ".", "..", "..."];
    let idx = 0;
    const id = setInterval(() => {
      idx = (idx + 1) % frames.length;
      setDots(frames[idx]);
    }, 250);
    return () => clearInterval(id);
  }, [loading]);

  const visibleMatches = useMemo(() => {
    return matches
      .filter((m) => !m.flag_bye)
      .sort((a, b) => {
        const tA = a.time_match ? moment(a.time_match, "HH:mm") : null;
        const tB = b.time_match ? moment(b.time_match, "HH:mm") : null;
        if (!tA || !tB) return 0;
        return tA.isBefore(tB) ? -1 : 1;
      });
  }, [matches]);

  /**
   * FTR-0028: el Centro de Programación materializa partidos ANTES del sorteo. Existen, se ven en
   * la lista y se pueden programar, pero no se pueden jugar: abrir la planilla o el marcador sin
   * participantes lleva a pantallas que fallan al leer `Institution`. Se avisa y no se abre.
   * Un BYE no entra aquí: no es un partido jugable y ya se muestra aparte.
   */
  const assertMatchPlayable = useCallback((m) => {
    if (m?.Participant1 && m?.Participant2) return true;
    toast.error("Este partido todavía no tiene participantes definidos.");
    return false;
  }, []);

  const handleOpenResultados = useCallback(
    (m) => {
      if (!assertMatchPlayable(m)) return;
      dispatch(setMatchSelected({ matchData: m }));
      setOpenResultados(true);
    },
    [dispatch, assertMatchPlayable]
  );
  const handleOpenSimpleScore = useCallback(
    (m) => {
      if (!assertMatchPlayable(m)) return;
      dispatch(setMatchSelected({ matchData: m }));
      setSimpleScoreMatch(m);
      setOpenSimpleScore(true);
    },
    [dispatch, assertMatchPlayable]
  );
  const handleOpenPlanillaVivo = useCallback(
    (m) => {
      if (!assertMatchPlayable(m)) return;
      dispatch(setMatchSelected({ matchData: m }));

      if (formattedSport === 5) {
        setOpenPlanillaVivo(true)
      } else if (formattedSport === 4) {
        setOpenPlanillaBasquetball(true)
      } else if (formattedSport === 1) {
        setOpenPlanillaFutbol(true)
      } else if (formattedSport === 2) {
        setOpenPlanillaFutsal(true)
      } else if (formattedSport === 6) {
        setOpenPlanillaRugby(true)
      } else if (formattedSport === 3) {
        setOpenPlanillaVoleyball(true)
      } else if (formattedSport === 7) {
        setOpenPlanillaHandballBeach(true)        
      } else {
        alert("No hay planilla en vivo para este deporte.");
      }
    },
    [dispatch, assertMatchPlayable, formattedSport]
  );
  const handleOpenStatsFut = useCallback(
    (m, tipo) => {
      dispatch(setMatchSelected({ matchData: m }));
      setTipoStat(tipo);
      setOpenStatsFut(true);
    },
    [dispatch]
  );
  const handleOpenStatsVoley = useCallback(
    (m, tipo) => {
      dispatch(setMatchSelected({ matchData: m }));
      setTipoStat(tipo);
      setOpenStatsVoley(true);
    },
    [dispatch]
  );

  /**
   * FTR-0060: PDF de la fecha/etapa y fase SELECCIONADAS (las mismas que usa la lista de partidos) con la tabla
   * de posiciones hasta esa fecha. El backend avisa si la tabla guardada de POSICIONES no coincide con el cálculo.
   */
  const handleDownloadResultadosPdf = useCallback(async () => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const { standingsStale } = await downloadResultadosFechaPdf({
        idevent,
        idsport,
        idtypephase: getCurrentIdTypePhase(),
        nroFecha: Number(selectedDate),
      });
      if (standingsStale) toast(STANDINGS_STALE_WARNING, { icon: "⚠️", duration: 8000 });
    } catch (err) {
      toast.error(await resultadosPdfErrorMessage(err));
    } finally {
      setDownloadingPdf(false);
    }
  }, [downloadingPdf, idevent, idsport, getCurrentIdTypePhase, selectedDate]);

  const resultadosPdfButton = (
    <ResultadosPdfButton onClick={handleDownloadResultadosPdf} loading={downloadingPdf} />
  );

  const { width, height } = useWindowSize();

  return (
    <>
      <div className="container_estadistica mx-auto p-4 font-quickSand">
        <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
          <Tab.List className="flex justify-center space-x-1 rounded-lg bg-gray-100 p-1">
            {config.nroFechasGrupo > 0 && (
              <Tab className={({ selected }) =>
                selected ? "tab_selected w-full py-2.5" : "tab_unselected w-full py-2.5"
              }>
                {+idevent === 208 ? "Partidos" : "Fase de Grupos"}
              </Tab>
            )}
            {config.nroEtapasFinal > 0 && (
              <Tab className={({ selected }) =>
                selected ? "tab_selected w-full py-2.5" : "tab_unselected w-full py-2.5"
              }>
                Fase de Eliminación
              </Tab>
            )}
          </Tab.List>

          <Tab.Panels className="mt-2">
            {config.nroFechasGrupo > 0 && (
              <Tab.Panel>
                <SelectorFechas
                  items={fechas}
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  label={(f) => `Fecha ${f}`}
                  action={resultadosPdfButton}
                />
              </Tab.Panel>
            )}
            {config.nroEtapasFinal > 0 && (
              <Tab.Panel key="eliminacion">
                <SelectorFechas
                  items={etapas}
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  label={(e) => e}
                  action={resultadosPdfButton}
                />
              </Tab.Panel>
            )}
          </Tab.Panels>
        </Tab.Group>

        {loading ? (
          <SkeletonGrid dots={dots} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-8">
            {visibleMatches.map((match) => (
              <MatchCard
                key={match.idmatch}
                match={match}
                moodName={config.moodName}
                useBandera={config.useBandera}
                formattedSport={formattedSport}
                onOpenResultados={handleOpenResultados}
                onOpenStatsFut={handleOpenStatsFut}
                onOpenStatsVoley={handleOpenStatsVoley}
                onOpenSimpleScore={handleOpenSimpleScore}
                onOpenPlanillaVivo={handleOpenPlanillaVivo}
              />
            ))}
          </div>
        )}
      </div>

      <Suspense fallback={null}>
        {openResultados && <FullScreenResultados open={openResultados} setOpen={() => setOpenResultados(false)} onUpdated={refreshMatches} idsport={idsport} />}
        {openStatsFut && <FullScreenFut open={openStatsFut} setOpen={() => setOpenStatsFut(false)} tipoStat={tipoStat} />}
        {openStatsVoley && <FullScreenVoley open={openStatsVoley} setOpen={() => setOpenStatsVoley(false)} tipoStat={tipoStat} />}
        {openSimpleScore && simpleScoreMatch && (
          <FullScreenSimpleScore
            open={openSimpleScore}
            setOpen={setOpenSimpleScore}
            match={simpleScoreMatch}
            onUpdated={refreshMatches}
          />
        )}
        {openPlanillaVivo && <FullScreenPlanillaHandball open={openPlanillaVivo} setOpen={() => setOpenPlanillaVivo(false)} />}
        {openPlanillaBasquetball && <FullScreenPlanillaBasquetball open={openPlanillaBasquetball} setOpen={() => setOpenPlanillaBasquetball(false)} />}
        {openPlanillaFutbol && <FullScreenPlanillaFutbol open={openPlanillaFutbol} setOpen={() => setOpenPlanillaFutbol(false)} />}
        {openPlanillaFutsal && <FullScreenPlanillaFutsal open={openPlanillaFutsal} setOpen={() => setOpenPlanillaFutsal(false)} />}
        {openPlanillaRugby && (
          <FullScreenPlanillaRugby
            open={openPlanillaRugby}
            setOpen={() => setOpenPlanillaRugby(false)}
            onOfficialized={refreshMatches}
          />
        )}
        {
          openPlanillaVoleyball && (
            <FullScreenPlanillaVoleyball open={openPlanillaVoleyball} setOpen={() => setOpenPlanillaVoleyball(false)} />
          )
        }
        {
          openPlanillaHandballBeach && (
            <FullScreenPlanillaHandballBeach open={openPlanillaHandballBeach} setOpen={() => setOpenPlanillaHandballBeach(false)} />
          )
        }
      </Suspense>

      {refreshing && (
        <div className="fixed bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded">
          Actualizando…
        </div>
      )}

    </>
  );
}

/* ---------- Selector reutilizable ---------- */
// `action` (FTR-0060) va a la derecha de los chips sin moverlos: desde lg, rejilla simétrica 1fr | chips | 1fr
// (cada lado con el ancho mínimo del botón, así nunca desborda: si faltara espacio, los chips se envuelven);
// por debajo de lg (tablet/móvil), el botón va centrado debajo de los chips.
const SelectorFechas = ({ items, selected, onSelect, label, action }) => (
  <div>
    <h4 className="title text-center text-3xl font-semibold text-gray-700 mb-4">
      FECHAS / ETAPAS
    </h4>

    <div
      className="mb-8 grid grid-cols-1 items-center gap-3 lg:grid-cols-[minmax(8.5rem,1fr)_auto_minmax(8.5rem,1fr)]"
      data-testid="fechas-row"
    >
      <div className="hidden lg:block" aria-hidden="true" />
      <div className="flex min-w-0 justify-center space-x-2 flex-wrap">
        {items.map((item, idx) => {
          const value = String(idx + 1);
          return (
            <button
              key={value}
              className={`fechas_button ${selected === value ? "selected_date" : "unselected_date"}`}
              onClick={() => onSelect(value)}
            >
              {label(item)}
            </button>
          );
        })}
      </div>
      {action ? <div className="flex justify-center lg:justify-end">{action}</div> : null}
    </div>
  </div>
);

/* ---------- Botón PDF de resultados (FTR-0060): sutil, no compite con los chips ---------- */
const ResultadosPdfButton = ({ onClick, loading }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    aria-busy={loading}
    aria-label="Descargar PDF de Resultados de la fecha seleccionada"
    title="Descargar en PDF los resultados de la fecha seleccionada y la tabla de posiciones"
    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-60"
  >
    {loading ? (
      <svg className="h-4 w-4 animate-spin text-red-600" viewBox="0 0 24 24" fill="none" aria-hidden="true" data-testid="resultados-pdf-spinner">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
      </svg>
    ) : (
      <FaFilePdf className="h-4 w-4 text-red-600" aria-hidden="true" />
    )}
    <span>Resultados</span>
  </button>
);

/* ---------- Placeholder mientras carga ---------- */
const SkeletonGrid = ({ dots }) => (
  <div className="w-full p-4">
    <h2 className="text-2xl font-semibold text-gray-700 mb-6 text-center">
      Cargando Enfrentamientos{dots}
    </h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="animate-pulse bg-white rounded-lg shadow-lg p-6 space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      ))}
    </div>
  </div>
);

export default ResultadosArea;
